import { useCallback, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ConfirmModal } from '@tanium/react-confirm-modal';
import { Form, FormRow } from '@tanium/react-form';
import { FormTextInput } from '@tanium/react-form-text-input';
import { Stack } from '@tanium/react-stack';
import { useToastNotificationsControl } from '@tanium/react-toast-notifications-context';
import { useAuthenticatedApiContext } from '../../api/useApiClient';
import { useAuthContext } from '../../auth';
import { AsyncStatus, useAsync } from '../../hooks/useAsync';

interface ModalProps {
  isOpen: boolean;
  workspace: string;
  onRequestClose: () => void;
  onDeleteFailed: () => void;
}

export const DeleteAccountModal: React.FC<ModalProps> = ({ isOpen, workspace, onRequestClose, onDeleteFailed }) => {
  const { t } = useTranslation();
  const apiClient = useAuthenticatedApiContext();
  const { signOut } = useAuthContext();
  const { showNotification } = useToastNotificationsControl();
  const [typedWorkspace, setTypedWorkspace] = useState('');

  const deleteAccount = useAsync(
    useCallback(async (): Promise<void> => {
      await apiClient.deleteTenantSelf();
    }, [apiClient]),
    { autoReset: true }
  );

  const formInitialValue = useMemo(() => ({ confirmation: typedWorkspace }), [typedWorkspace]);

  useEffect(() => {
    if (deleteAccount.status === AsyncStatus.Success) {
      showNotification({ type: 'success', contents: t('Successfully deleted your Tanium Account') });
      signOut();
    } else if (deleteAccount.status === AsyncStatus.Failure) {
      showNotification({ type: 'error', contents: t('Failed to delete your Tanium Account') });
      deleteAccount.reset();
      onDeleteFailed();
    }
  }, [deleteAccount.status]);

  useEffect(() => {
    if (!isOpen) {
      setTypedWorkspace('');
    }
  }, [isOpen]);

  const handleCancel = () => {
    setTypedWorkspace('');
    onRequestClose();
  };

  const isConfirmationMatching = typedWorkspace.trim() === workspace;

  return (
    <ConfirmModal
      isOpen={isOpen}
      header={t('Delete Account')}
      confirmLabel={t('Delete Account')}
      onConfirm={async () => {
        await deleteAccount.execute();
      }}
      isConfirmDisabled={
        !isConfirmationMatching ||
        deleteAccount.status === AsyncStatus.Pending ||
        deleteAccount.status === AsyncStatus.Success
      }
      onCancel={handleCancel}
    >
      <Stack direction="column" itemSpacing={2}>
        <span>
          <Trans
            i18nKey="This permanently deletes your Tanium Account workspace. This cannot be undone."
            values={{ workspace }}
          >
            This permanently deletes your Tanium Account. This cannot be undone.
          </Trans>
        </span>
        <span>
          <Trans
            i18nKey="The workspace name workspace stays reserved for about a month and cannot be reused before then."
            values={{ workspace }}
          >
            The workspace name stays reserved for about a month and cannot be reused before then.
          </Trans>
        </span>
        <Form
          initialValue={formInitialValue}
          buttons={<></>}
          onValueChange={(e) => {
            setTypedWorkspace(e.value.confirmation as string);
          }}
        >
          <FormRow dataKey="confirmation" label={t('Type workspace to confirm', { workspace })} required>
            <FormTextInput />
          </FormRow>
        </Form>
      </Stack>
    </ConfirmModal>
  );
};
